import {
  pgTable,
  text,
  timestamp,
  uuid,
  integer,
  index,
  check,
  primaryKey,
} from "drizzle-orm/pg-core";
import { sql } from "drizzle-orm";
import { sites } from "./page-builder-sites";

const tz = { withTimezone: true } as const;

// ---------------------------------------------------------------------------
// Job status enum
// ---------------------------------------------------------------------------

export const SITE_PUBLISH_JOB_STATUSES = [
  "queued",
  "running",
  "completed",
  "partial_failure",
  "failed",
] as const;

export type SitePublishJobStatus = (typeof SITE_PUBLISH_JOB_STATUSES)[number];

// ---------------------------------------------------------------------------
// Site publish jobs
// ---------------------------------------------------------------------------

export const sitePublishJobs = pgTable(
  "site_publish_jobs",
  {
    id: uuid("id").primaryKey().defaultRandom(),
    tenantId: uuid("tenant_id").notNull(),
    siteId: uuid("site_id")
      .notNull()
      .references(() => sites.id, { onDelete: "cascade" }),
    status: text("status").notNull().default("queued"),
    totalPages: integer("total_pages").notNull(),
    completedPages: integer("completed_pages").notNull().default(0),
    failedPages: integer("failed_pages").notNull().default(0),
    actorId: text("actor_id").notNull(),
    startedAt: timestamp("started_at", tz),
    completedAt: timestamp("completed_at", tz),
    createdAt: timestamp("created_at", tz).notNull().defaultNow(),
    updatedAt: timestamp("updated_at", tz).notNull().defaultNow(),
  },
  (table) => [
    index("idx_site_publish_jobs_site").on(table.siteId),
    index("idx_site_publish_jobs_tenant_status").on(
      table.tenantId,
      table.status,
    ),
    check(
      "chk_site_publish_job_status",
      sql`${table.status} IN ('queued', 'running', 'completed', 'partial_failure', 'failed')`,
    ),
  ],
);

// ---------------------------------------------------------------------------
// Site publish page renders (idempotency tracking)
// ---------------------------------------------------------------------------
// Composite PK (job_id, page_id) enforces exactly-once counter increments.
// On queue retry, INSERT ... ON CONFLICT DO NOTHING returns empty — skip counter.

export const sitePublishPageRenders = pgTable(
  "site_publish_page_renders",
  {
    jobId: uuid("job_id")
      .notNull()
      .references(() => sitePublishJobs.id, { onDelete: "cascade" }),
    pageId: uuid("page_id").notNull(),
    sitePath: text("site_path").notNull(),
    renderedAt: timestamp("rendered_at", tz).notNull().defaultNow(),
  },
  (table) => [
    primaryKey({ columns: [table.jobId, table.pageId] }),
    index("idx_spr_job").on(table.jobId),
  ],
);
